import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface ClientNotification {
  id: string;
  title: string;
  message: string | null;
  notification_type: string;
  is_read: boolean;
  created_at: string;
}

interface Props {
  clientId: string;
  onUnreadCountChange?: (count: number) => void;
}

type FilterType = 'all' | 'stage_advance' | 'balance_alert' | 'document_confirmed' | 'message' | 'info';

const TYPE_CONFIG: Record<string, { icon: string; bg: string; border: string; iconColor: string; label: string }> = {
  stage_advance: {
    icon: 'ri-rocket-line',
    bg: 'bg-[#FDB913]/10',
    border: 'border-[#FDB913]/30',
    iconColor: 'text-[#FDB913]',
    label: 'Case Update',
  },
  balance_alert: {
    icon: 'ri-alarm-warning-line',
    bg: 'bg-red-500/10',
    border: 'border-red-500/25',
    iconColor: 'text-red-400',
    label: 'Balance Alert',
  },
  document_confirmed: {
    icon: 'ri-folder-check-line',
    bg: 'bg-green-500/10',
    border: 'border-green-500/25',
    iconColor: 'text-green-400',
    label: 'Document',
  },
  message: {
    icon: 'ri-mail-line',
    bg: 'bg-white/8',
    border: 'border-white/15',
    iconColor: 'text-white',
    label: 'Message',
  },
  info: {
    icon: 'ri-information-line',
    bg: 'bg-white/5',
    border: 'border-white/10',
    iconColor: 'text-white/60',
    label: 'Info',
  },
};

const FILTER_OPTIONS: { value: FilterType; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'stage_advance', label: 'Case Updates' },
  { value: 'balance_alert', label: 'Balance Alerts' },
  { value: 'document_confirmed', label: 'Documents' },
  { value: 'message', label: 'Messages' },
];

export default function NotificationsPanel({ clientId, onUnreadCountChange }: Props) {
  const [notifications, setNotifications] = useState<ClientNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterType>('all');
  const [markingAll, setMarkingAll] = useState(false);

  useEffect(() => {
    if (!clientId) return;
    loadNotifications();
  }, [clientId]);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from('client_notifications')
        .select('*')
        .eq('client_id', clientId)
        .order('created_at', { ascending: false });
      const notifs = (data || []) as ClientNotification[];
      setNotifications(notifs);
      onUnreadCountChange?.(notifs.filter((n) => !n.is_read).length);
    } catch (err) {
      console.error('Notifications load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id: string) => {
    await supabase.from('client_notifications').update({ is_read: true }).eq('id', id);
    setNotifications((prev) => {
      const updated = prev.map((n) => (n.id === id ? { ...n, is_read: true } : n));
      onUnreadCountChange?.(updated.filter((n) => !n.is_read).length);
      return updated;
    });
  };

  const markAllRead = async () => {
    const unreadIds = notifications.filter((n) => !n.is_read).map((n) => n.id);
    if (unreadIds.length === 0) return;
    setMarkingAll(true);
    await supabase.from('client_notifications').update({ is_read: true }).in('id', unreadIds);
    setNotifications((prev) => {
      const updated = prev.map((n) => ({ ...n, is_read: true }));
      onUnreadCountChange?.(0);
      return updated;
    });
    setMarkingAll(false);
  };

  const filtered =
    filter === 'all'
      ? notifications
      : notifications.filter((n) => n.notification_type === filter);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  return (
    <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h3 className="text-2xl font-black text-white">Notifications</h3>
          {unreadCount > 0 && (
            <span className="min-w-[24px] h-6 bg-red-500 text-white text-xs font-black rounded-full flex items-center justify-center px-1.5 animate-pulse">
              {unreadCount}
            </span>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllRead}
            disabled={markingAll}
            className="flex items-center gap-1.5 text-xs font-bold text-[#FDB913] hover:text-[#e6a800] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            {markingAll ? (
              <i className="ri-loader-4-line animate-spin"></i>
            ) : (
              <i className="ri-checkbox-multiple-line"></i>
            )}
            Mark all read
          </button>
        )}
      </div>

      {/* Filter tabs */}
      <div className="flex items-center gap-2 mb-6 flex-wrap">
        {FILTER_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => setFilter(opt.value)}
            className={`px-3 py-1.5 rounded-lg text-xs font-black transition-all cursor-pointer whitespace-nowrap ${
              filter === opt.value
                ? 'bg-[#FDB913] text-black'
                : 'bg-white/5 text-white/60 hover:bg-white/10 border border-white/10'
            }`}
          >
            {opt.label}
            {opt.value !== 'all' && notifications.filter((n) => n.notification_type === opt.value && !n.is_read).length > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center w-4 h-4 bg-red-500 text-white text-[9px] font-black rounded-full">
                {notifications.filter((n) => n.notification_type === opt.value && !n.is_read).length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-[#FDB913] border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-white/5 rounded-2xl border border-white/10">
            <i className="ri-notification-off-line text-white/20 text-3xl"></i>
          </div>
          <p className="text-white/40 text-sm font-bold">No notifications yet</p>
          <p className="text-white/25 text-xs mt-1">
            Automated alerts will appear here as your case progresses
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((n) => {
            const cfg = TYPE_CONFIG[n.notification_type] ?? TYPE_CONFIG.info;
            const unread = !n.is_read;
            return (
              <div
                key={n.id}
                onClick={() => unread && markAsRead(n.id)}
                className={`flex items-start gap-4 p-4 rounded-xl border transition-all ${
                  unread
                    ? `${cfg.bg} ${cfg.border} cursor-pointer hover:opacity-90`
                    : 'bg-white/3 border-white/8 opacity-60 cursor-default'
                }`}
              >
                {/* Icon */}
                <div
                  className={`w-10 h-10 flex items-center justify-center rounded-xl border flex-shrink-0 ${
                    unread ? `${cfg.bg} ${cfg.border}` : 'bg-white/5 border-white/10'
                  }`}
                >
                  <i className={`${cfg.icon} text-base ${unread ? cfg.iconColor : 'text-white/25'}`}></i>
                </div>

                {/* Text */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <p className={`text-sm font-black leading-snug ${unread ? 'text-white' : 'text-white/40'}`}>
                      {n.title}
                    </p>
                    {unread && <span className="w-2 h-2 bg-[#FDB913] rounded-full flex-shrink-0 mt-1.5"></span>}
                  </div>
                  {n.message && (
                    <p className={`text-xs leading-relaxed mb-1.5 ${unread ? 'text-white/60' : 'text-white/25'}`}>
                      {n.message}
                    </p>
                  )}
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-xs font-bold px-2 py-0.5 rounded-full border ${
                        unread ? `${cfg.iconColor} ${cfg.border} bg-white/5` : 'text-white/25 border-white/10'
                      }`}
                    >
                      {cfg.label}
                    </span>
                    <span className="text-white/25 text-xs">{formatDate(n.created_at)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
